import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect, useState } from 'react'
import { scrollToTop } from 'src/utils/commonMethods'
import { getTokenForQrReport } from 'src/services/individualService'
import LoadingModal from 'src/components/common/LoadingModal'
import {
  fetchTokenForBuyPolicy,
  getKycStatus,
  getPolicyDownload,
  saveISNPStatus,
  updatePaymentInfo,
} from 'src/services/buyPolicyService'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { showNotificationMessage, updateKycLink } from 'src/actions'
import PaymentSuccessImg from 'src/assets/Buy_Policy_Images/PaymentSuccessImg.svg'
import PaymentSuccessReverse from 'src/assets/Buy_Policy_Images/paymentSuccessReverse.svg'
import fingersCross from 'src/assets/Buy_Policy_Images/fingersCross.svg'
import contactId from 'src/assets/Buy_Policy_Images/ContactId.svg'
import contactPassword from 'src/assets/Buy_Policy_Images/ContactPassword.svg'
import {
  CURRENT_STATUS,
  ENCOUNTERED_ERROR_WHILE_SAVING,
  FETCHING_ERROR_KYC,
  INSURANCE_TYPE_CATOGORIES,
  KYC_DATA,
  PAYMENT_STATUS,
} from 'src/utils/constants/BuyPolicy'
import { COLORS } from 'src/utils/Themes/colors'
import { NOTIFICATION_TYPES } from 'src/utils/constants/common'
import { getISNPStatusRequestBody } from 'src/utils/commonMethods/buyPolicy'
import { NOTIFICATION_MSG_REPORTS } from 'src/utils/constants/CustomerReports'

const PaymentSuccess = () => {
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [kycData, setKycData] = useState({})
  const [token, setToken] = useState()

  const { search } = useLocation()

  const searchParams = React.useMemo(
    () => new URLSearchParams(search),
    [search]
  )
  const policyNumber = searchParams.get('policyNumber')
  const transactionNumber = searchParams.get('transactionNumber')
  const enquiryId = searchParams.get('enquiryId')
  const guestUserDetails = useSelector((state) => state?.buyPolicyReducer)

  const dispatch = useDispatch()

  useEffect(() => {
    scrollToTop()
    fetchToken()
    saveMetadataAndNavigate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchToken = async () => {
    setLoading(true)

    try {
      const response = await getTokenForQrReport()

      if (response?.res?.status !== 200) {
        setLoading(false)
        dispatch(
          showNotificationMessage({
            type: NOTIFICATION_TYPES?.ERROR,
            message: NOTIFICATION_MSG_REPORTS?.PLEASE_TRY_AGAIN_AFTER_SOMETIME,
          })
        )
        return // Exit early if the response is not successful
      }

      // Success
      setLoading(false)
      const accessToken = response?.res?.data?.access_token
      setToken(accessToken)
      updatePaymentStatus(accessToken)
    } catch (err) {
      dispatch(
        showNotificationMessage({
          type: NOTIFICATION_TYPES?.ERROR,
          message: NOTIFICATION_MSG_REPORTS?.ENCOUNTERED_ERROR_ON_TOKEN_FETCH,
        })
      )
    }
  }

  const updatePaymentStatus = async (token) => {
    setLoading(true)

    const requestBody = {
      policyId: policyNumber,
      transactionNumber: transactionNumber,
      success: true,
    }

    try {
      const response = await updatePaymentInfo(token, requestBody)

      if (response?.res?.status !== 200) {
        setLoading(false)
        dispatch(
          showNotificationMessage({
            type: NOTIFICATION_TYPES?.ERROR,
            message: NOTIFICATION_MSG_REPORTS?.PLEASE_TRY_AGAIN_AFTER_SOMETIME,
          })
        )
        return // Exit early if the response is not successful
      }

      // Success
      const responseData = response?.res?.data
      setLoading(false)

      if (responseData?.success) {
        fetchKycStatus(token)
      } else {
        dispatch(
          showNotificationMessage({
            type: NOTIFICATION_TYPES?.ERROR,
            message: responseData?.message,
          })
        )
      }
    } catch (err) {
      setLoading(false)
      dispatch(
        showNotificationMessage({
          type: NOTIFICATION_TYPES?.ERROR,
          message: PAYMENT_STATUS,
        })
      )
    }
  }

  const fetchKycStatus = async (token) => {
    setLoading(true)

    try {
      const response = await getKycStatus(token, policyNumber)

      if (response?.res?.status !== 200) {
        setLoading(false)
        dispatch(
          showNotificationMessage({
            type: NOTIFICATION_TYPES?.ERROR,
            message: NOTIFICATION_MSG_REPORTS?.PLEASE_TRY_AGAIN_AFTER_SOMETIME,
          })
        )
        return // Exit early if the response is not successful
      }

      // Success
      const responseData = response?.res?.data
      setLoading(false)

      if (responseData?.success) {
        setKycData(responseData?.data)
      } else {
        dispatch(
          showNotificationMessage({
            type: NOTIFICATION_TYPES?.ERROR,
            message: responseData?.message,
          })
        )
      }
    } catch (err) {
      setLoading(false)
      dispatch(
        showNotificationMessage({
          type: NOTIFICATION_TYPES?.ERROR,
          message: FETCHING_ERROR_KYC,
        })
      )
    }
  }

  const redirectToKYC = () => {
    if (kycData?.link) {
      dispatch(updateKycLink(kycData?.link))
      window.open(kycData?.link, '_self')
    }
  }

  const redirectToLogin = () => {
    window.open(process?.env?.REACT_APP_TROVITY_LOGIN_URL, '_self')
  }

  const downloadPolicyDocument = async () => {
    const response = await getPolicyDownload(token, enquiryId)

    try {
      if (response?.res?.status !== 200) {
        setLoading(false)
        dispatch(
          showNotificationMessage({
            type: NOTIFICATION_TYPES?.ERROR,
            message: NOTIFICATION_MSG_REPORTS?.PLEASE_TRY_AGAIN_AFTER_SOMETIME,
          })
        )
      } else {
        setLoading(false)
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(new Blob([response?.res?.data]))
        link.setAttribute('download', 'PolicyDocument.pdf')
        link.setAttribute('target', '_blank')
        document.body.appendChild(link)
        link.click()
      }
    } catch (err) {
      setLoading(false)
      dispatch(
        showNotificationMessage({
          type: NOTIFICATION_TYPES?.ERROR,
          message:
            NOTIFICATION_MSG_REPORTS?.ENCOUNTERED_ERROR_ON_POLICY_DETAILS,
        })
      )
    }
  }

  const saveMetadataAndNavigate = () => {
    setLoading(true)

    Promise.resolve(fetchTokenForBuyPolicy())
      .then((token) => {
        setLoading(false)
        saveISNPStatusTasks(token)
      })
      .catch(() => {
        setLoading(false)
        dispatch(
          showNotificationMessage({
            type: NOTIFICATION_TYPES?.ERROR,
            message: NOTIFICATION_MSG_REPORTS?.ENCOUNTERED_ERROR_ON_TOKEN_FETCH,
          })
        )
      })
  }

  const saveISNPStatusTasks = async (token) => {
    setLoading(true)
    const formData = getISNPStatusRequestBody(
      guestUserDetails,
      INSURANCE_TYPE_CATOGORIES?.health,
      CURRENT_STATUS?.paymentSuccessKycPending
    )

    try {
      const response = await saveISNPStatus(token, formData)
      return response?.res?.data
    } catch (error) {
      // If there is any error from the backend it will be handled here
      dispatch(
        showNotificationMessage({
          type: NOTIFICATION_TYPES?.ERROR,
          message: ENCOUNTERED_ERROR_WHILE_SAVING,
        })
      )
    } finally {
      setLoading(false) // Ensure loading is set to false in both success and error cases
    }
  }

  return (
    <>
      <LoadingModal loading={loading} />
      <div className={classes.mainContainer}>
        <div className={classes.successContainer}>
          <div className={classes.successContent}>
            <div className={classes.imageContainer}>
              <img
                src={PaymentSuccessImg}
                alt='one more step'
                className={'w-100'}
              />
              <img
                src={PaymentSuccessReverse}
                alt='Shadow'
                className={`w-100 ${classes.shadowImage}`}
              />
            </div>
            <div className={classes.headingContainer}>
              <div className={classes.heading}>
                <span>Payment successful</span>
              </div>
              <div className={classes.providerHeading}>
                <span className={classes.inputContent}>
                  Policy ID:{policyNumber}
                </span>
                <span className={classes.inputContent}>
                  {' '}
                  Transaction ID: {transactionNumber}
                </span>
                &nbsp;
              </div>
              {kycData?.kycVerificationStatus === KYC_DATA?.notDone ? (
                <div className={classes.contentContainer}>
                  <div className={classes.butText}>but</div>
                  <div className={classes.textContent}>
                    <span className={classes.text}>
                      To activate your policy,<br></br>
                      <span className={classes.subText}>
                        Please complete your KYC
                      </span>
                    </span>
                  </div>
                  <div
                    className={classes.pleaseText}
                    onClick={redirectToKYC}
                    id='kycRedirect'
                  >
                    <span>Please Click Here</span>
                  </div>
                </div>
              ) : null}
              {kycData?.kycVerificationStatus === KYC_DATA?.done ? (
                <div className={classes.contentContainer}>
                  <div className={classes.kycText}>KYC has been Verified</div>
                  {enquiryId && (
                    <div className={classes.downloadContainer}>
                      Please&nbsp;
                      <span
                        className={classes.downloadClick}
                        onClick={downloadPolicyDocument}
                      >
                        click here
                      </span>
                      &nbsp; to download your policy
                    </div>
                  )}
                  <div className={classes.textContent}>
                    <span
                      className={classes.loginText}
                      onClick={redirectToLogin}
                      id='kycRedirect'
                    >
                      Log in
                    </span>
                    &nbsp; into your Trovity account to view & download your
                    policy
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div className={classes.viewContainer}>
          <div className={classes.ViewContent}>
            <span className={classes.viewingText}>
              A Trovity account has been created for viewing policies.
            </span>
            <br></br>
            <span className={classes.logInText}>
              Log in with the details sent to your email
            </span>
          </div>
          <div className={classes.descriptionContainer}>
            <div className={classes.logInContainer}>
              <span className={classes.logIn}>LOGIN </span>
              <br></br>
              <span className={classes.loginDescription}>
                Manage / Purchase your Insurances and Claims
                <div className={classes.crossFinger}>
                  <img
                    src={fingersCross}
                    alt='fingersCross'
                    className={classes.crossFingerImg}
                  />
                  <span className={classes.crossText}>
                    (<b>fingers crossed</b> you won&apos;t need that last one!)
                  </span>
                </div>
              </span>
            </div>
            <div className={classes.sampleContainer}>
              <div className={classes.sampleText}>
                <img src={contactId} alt='contactId' />
                <div>
                  <span className={classes.idText}>ID</span>
                  <br></br>
                  <span className={classes.id}>xxxxxx@xxxx.com</span>
                </div>
              </div>
              <div className={classes.sampleText}>
                <img src={contactPassword} alt='contactPassword' />
                <div>
                  <span className={classes.idText}>Password</span>
                  <br></br>
                  <span className={classes.id}>****************</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default PaymentSuccess

const useStyles = makeStyles({
  successContainer: {
    background: 'linear-gradient(185.98deg, #CEF3FF -18.56%, #FFFFFF 67.98%)',
    border: 'none',
    width: '60%',
    padding: '30px',
    boxShadow: '0px 6px 8px 0px #4DC3E333',
    borderRadius: '10px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '30px',
  },
  mainContainer: {
    padding: '40px 60px 40px 60px',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
  successContent: {
    display: 'flex',
    '@media (max-width:800px)': {
      flexDirection: 'column',
    },
  },
  imageContainer: {
    width: '40%',
    marginTop: '-11%',
    maxHeight: '360px',
    overflow: 'hidden',
    '@media (max-width:800px)': {
      width: '100%',
    },
  },
  headingContainer: {
    width: '60%',
    '@media (max-width:800px)': {
      width: '100%',
    },
  },
  heading: {
    color: '#02A387',
    fontSize: '23px',
    padding: '10px',
    fontWeight: '600',
    textAlign: 'center',
  },
  headingContainerOther: {
    marginBottom: '8px',
    display: 'flex',
  },
  subHeading: {
    fontSize: '18px',
    color: '#000000',
    textTransform: 'uppercase',
  },
  headingContent: {
    fontSize: '13px',
    border: '1px solid',
    borderRadius: '5px',
    textAlign: 'center',
    padding: '5px 10px 5px 10px',
    width: '100%',
    color: '#48B7D4',
    background: 'white',
  },
  inputContent: {
    fontSize: '12px',
    color: '#585858',
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  content: {
    textAlign: 'center',
  },
  input: {
    display: 'flex',
    justifyContent: 'center',
  },
  shadowImage: {
    '@media (max-width:800px)': {
      display: 'none',
    },
  },
  kycImage: {
    width: '100%',
  },
  kycLink: {
    fontSize: '20px',
    color: '#54C5E3',
    border: 'none !important',
    background: 'white !important',
    textDecoration: 'underline !important',
    cursor: 'pointer',
  },
  kycContent: {
    color: '#9AAFB5',
    fontSize: '14px',
  },
  kycContainer: {
    display: 'flex',
    background: 'white',
    border: '1px dashed #C0C0C0',
    borderRadius: '10px',
    padding: '30px 80px 30px 80px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  kycDoneContainer: {
    display: 'flex',
    background: 'white',
    border: '1px dashed #C0C0C0',
    borderRadius: '10px',
    padding: '30px 80px 30px 80px',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  kycDoneHeading: {
    display: 'flex',
    padding: '30px',
    gap: '10%',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  kycDoneTextContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  kycHeading: {
    fontSize: '37px',
    color: '#4FB9D6',
  },
  log: {
    fontSize: '22px',
    color: '#3EAFCD',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  kycDoneContent: {
    fontSize: '20px',
    textAlign: 'center',
  },
  kycDoneSubContent: {
    fontSize: '15px',
    textAlign: 'center',
    marginTop: '5px',
  },
  providerHeading: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: '10px',
    borderRadius: '10px',
    border: '1px solid #FFFFFF',
    boxShadow: '0px 4px 4px 0px #00000026',
  },
  butText: {
    color: '#B92250',
    fontSize: '22px',
    fontWeight: '900',
  },
  text: {
    fontSize: '16px',
    color: '#B92250',
    fontWeight: '400,',
  },
  subText: {
    fontSize: '21px',
    color: '#B92250',
    fontWeight: '900',
  },
  pleaseText: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#2397B7',
    textDecoration: 'underline',
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  loginText: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#2397B7',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  contentContainer: {
    textAlign: 'center',
    padding: '20px',
  },
  textContent: {
    padding: '15px',
  },
  ViewContent: {
    padding: '20px',
    textAlign: 'center',
  },
  viewingText: {
    fontSize: '17px',
    color: '#000000',
    fontWeight: '600',
  },
  logInText: {
    fontSize: '17px',
    color: '#000000',
    fontWeight: '700',
  },
  descriptionContainer: {
    border: '1px solid #B92250',
    borderRadius: '20px',
    display: 'flex',
  },
  crossFinger: {
    display: 'flex',
    gap: '5px',
  },
  crossFingerImg: {
    width: '15px',
  },
  crossText: {
    fontSize: '13px',
  },
  sampleContainer: {
    border: '1px solid #B92250',
    borderRadius: '20px',
    background: '#B92250',
    padding: '10px 30px',
  },
  sampleText: {
    display: 'flex',
    gap: '10px',
    marginBottom: '10px',
  },
  idText: {
    fontSize: '11px',
    color: '#54E0FF',
    fontWeight: '500',
  },
  id: {
    fontSize: '14px',
    color: '#FFFFFF',
    fontWeight: '500',
  },
  logInContainer: {
    padding: '20px',
    lineHeight: '22px',
  },
  logIn: {
    fontSize: '15px',
    color: '#B92250',
    fontWeight: '600',
  },
  loginDescription: {
    fontSize: '14px',
    lineHeight: '23px',
  },
  loginSubDescription: {
    fontSize: '11px',
  },
  viewContainer: {
    padding: '50px',
  },
  kycText: {
    fontSize: '21px',
    color: '#B92250',
    fontWeight: '900',
  },
  downloadContainer: {
    textAlign: 'center',
    marginBottom: '10px',
  },
  downloadClick: {
    color: COLORS.DARK_PINK,
    cursor: 'pointer',
  },
})
